import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';
import { QualityDialog } from './CsCallsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

export default function CsQualityTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/cs-control/quality/recent`, { headers: authHeaders() });
        setItems(r.data.items || []);
      } catch (_) { toast.error('Errore caricamento valutazioni'); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-500" /></div>;

  const scored = items.filter(i => typeof (i.scores || {}).punteggio === 'number');
  const avg = scored.length ? (scored.reduce((s, i) => s + i.scores.punteggio, 0) / scored.length).toFixed(1) : null;
  const byOp = {};
  scored.forEach(i => {
    const op = i.operator_name || 'Non attribuito';
    byOp[op] = byOp[op] || [];
    byOp[op].push(i.scores.punteggio);
  });

  return (
    <div className="space-y-4" data-testid="cs-quality-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase">Chiamate analizzate</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="cs-quality-count">{items.length}</p>
        </div>
        <div className="bg-indigo-600 rounded-xl p-4 text-white">
          <p className="text-xs uppercase opacity-70">Punteggio medio</p>
          <p className="text-2xl font-bold" data-testid="cs-quality-avg">{avg ?? '—'}<span className="text-sm opacity-60">/10</span></p>
        </div>
        {Object.entries(byOp).slice(0, 2).map(([op, arr]) => (
          <div key={op} className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-500 uppercase truncate">{op}</p>
            <p className="text-2xl font-bold text-slate-800">{(arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(1)}<span className="text-sm text-slate-400">/10</span></p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-slate-700">Valutazioni recenti (campione automatico notturno + manuali)</h3>
        </div>
        {items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400">Nessuna chiamata ancora analizzata. Il campione automatico gira ogni notte alle 3:45 — oppure usa "Trascrivi" nel tab Chiamate.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Data chiamata</th>
                  <th className="text-left px-3 py-2">Operatore</th>
                  <th className="text-left px-3 py-2">Motivo</th>
                  <th className="text-center px-3 py-2">Punteggio</th>
                  <th className="text-left px-3 py-2">Commento AI</th>
                  <th className="text-center px-3 py-2">Tipo</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const s = it.scores || {};
                  return (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setDetail({ id: it.call_id })} data-testid={`cs-quality-row-${i}`}>
                      <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                        {it.started_at ? new Date(it.started_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' }) : '—'}
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-700">{it.operator_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">{s.motivo_chiamata || '—'} {s.criticita && '🚩'}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${(s.punteggio ?? 0) >= 7 ? 'bg-emerald-100 text-emerald-700' : (s.punteggio != null ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400')}`}>
                          {s.punteggio ?? '—'}/10
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-xs max-w-md truncate">{s.commento || '—'}</td>
                      <td className="px-3 py-2 text-center text-xs text-slate-400">{it.trigger === 'auto' ? '🌙 auto' : '👆 manuale'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {detail && <QualityDialog call={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
